<template>
  <v-dialog
    v-model="dialog"
    max-width="420"
    persistent
    content-class="facility-select-dialog"
  >
    <v-card class="facility-dialog">
      <v-card-title class="facility-dialog__title">
        <v-icon left color="warning">mdi-map-marker-multiple</v-icon>
        <span>Select Facility</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="facility-dialog__body">
        <p class="facility-dialog__description">
          This task is linked to tags available in multiple facilities.
          Choose where to view:
        </p>

        <v-select
          v-model="selectedFacility"
          :items="facilityOptions"
          item-text="text"
          item-value="value"
          label="Facility"
          outlined
          dense
          hide-details
          class="facility-dialog__select"
        >
          <template v-slot:selection="{ item }">
            <v-icon left small :color="item.value === 'current' ? 'primary' : 'warning'">
              {{ item.value === 'current' ? 'mdi-cube-outline' : 'mdi-open-in-new' }}
            </v-icon>
            <span>{{ item.text }}</span>
          </template>

          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-icon class="mr-3">
                <v-icon small :color="item.value === 'current' ? 'primary' : 'warning'">
                  {{ item.value === 'current' ? 'mdi-cube-outline' : 'mdi-open-in-new' }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle v-if="item.value === 'current'">
                  Isolate in current view
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else>
                  Opens in new tab
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="facility-dialog__actions">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!selectedFacility"
          @click="confirm"
        >
          <v-icon left small>
            {{ selectedFacility === 'current' ? 'mdi-cube-outline' : 'mdi-open-in-new' }}
          </v-icon>
          {{ selectedFacility === 'current' ? 'Isolate' : 'Open' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapFacilitiesToObjects } from '@Task/utils/taskIsolation';

export default {
  name: 'FacilitySelectDialog',

  data() {
    return {
      dialog: false,
      selectedFacility: null,
      facilityOptions: [],
      task: null,
      resolvePromise: null
    };
  },

  methods: {
    /**
     * Opens the dialog and returns a promise that resolves with the user's selection.
     *
     * @param {Object} task - The task object
     * @param {Object} isolationInfo - Result from getTaskIsolationInfo
     * @param {Object} facilityDict - Map of facility IDs to names
     * @returns {Promise<Object>} Selection result: { cancelled, facilityId, isCurrent, task }
     */
    open(task, isolationInfo, facilityDict = {}) {
      this.task = task;
      this.facilityOptions = [];

      // Add current facility first (if tag exists locally)
      if (isolationInfo.hasCurrentFacility) {
        this.facilityOptions.push({
          value: 'current',
          text: 'Current Facility'
        });
      }

      // Add other facilities
      const otherFacilities = mapFacilitiesToObjects(
        isolationInfo.otherAssets,
        facilityDict
      );
      otherFacilities.forEach(facility => {
        this.facilityOptions.push({
          value: facility.id,
          text: facility.name
        });
      });

      // Default selection: Current Facility if available, otherwise first other facility
      this.selectedFacility = this.facilityOptions[0]?.value || null;
      this.dialog = true;

      return new Promise(resolve => {
        this.resolvePromise = resolve;
      });
    },

    /**
     * Cancel and close the dialog.
     */
    cancel() {
      this.dialog = false;
      this.resolvePromise?.({ cancelled: true });
      this.cleanup();
    },

    /**
     * Confirm selection and close the dialog.
     */
    confirm() {
      const isCurrent = this.selectedFacility === 'current';
      this.dialog = false;
      this.resolvePromise?.({
        cancelled: false,
        facilityId: isCurrent ? null : this.selectedFacility,
        isCurrent,
        task: this.task
      });
      this.cleanup();
    },

    /**
     * Cleanup internal state after dialog closes.
     */
    cleanup() {
      this.resolvePromise = null;
      // Reset state after animation completes
      setTimeout(() => {
        this.task = null;
        this.facilityOptions = [];
        this.selectedFacility = null;
      }, 300);
    }
  }
};
</script>

<style lang="scss" scoped>
// ═══════════════════════════════════════════════════════════
// FACILITY SELECT DIALOG
// Select dropdown for cross-facility tag isolation
// ═══════════════════════════════════════════════════════════

.facility-dialog {
  border-radius: 12px !important;
  overflow: hidden;

  &__title {
    padding: 16px 24px !important;
    font-size: 16px;
    font-weight: 600;
    background: linear-gradient(135deg, rgba(217, 119, 6, 0.08) 0%, rgba(217, 119, 6, 0.03) 100%);

    .v-icon {
      margin-right: 8px;
    }
  }

  &__body {
    padding: 20px 24px !important;
  }

  &__description {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
    margin-bottom: 16px;
    line-height: 1.5;
  }

  &__select {
    margin-top: 8px;

    ::v-deep .v-input__slot {
      min-height: 44px !important;
    }

    ::v-deep .v-select__selection {
      display: flex;
      align-items: center;
    }
  }

  &__actions {
    padding: 12px 16px !important;
    background: rgba(0, 0, 0, 0.02);
  }
}

// ─── Dark Theme Support ───
.theme--dark {
  .facility-dialog {
    &__title {
      background: linear-gradient(135deg, rgba(217, 119, 6, 0.15) 0%, rgba(217, 119, 6, 0.05) 100%);
    }

    &__description {
      color: rgba(255, 255, 255, 0.7);
    }

    &__actions {
      background: rgba(255, 255, 255, 0.03);
    }
  }
}
</style>
